import { AuthLayout } from '@components/layout';
import { DashboardLayout, SpecialistActiveGuard } from '@components/layout/dashboardLayout';
import { NotFoundPage } from '@components/shared/NotFoundPage/NotFoundPage';
import { CongratulationsForm, LoginForm, ResetForm, ResetPassword } from '@features/auth/login';
import LoginEmailConfirmation from '@features/auth/login/components/LoginEmailConfirmation';
import {
	CompanyRegistrationLayout,
	CompanyStepPersonalDetails,
} from '@features/auth/registration/company';
import { CompanyRegistrationResumeStep } from '@features/auth/registration/company/CompanyRegistrationResumeStep';
import { FarmerPersonalDetailsNew } from '@features/auth/registration/farmer/FarmerPersonalDetails';
import FarmerRegistrationLayout from '@features/auth/registration/farmer/FarmerRegistrationLayout';
import SpecialistRegistrationLayout from '@features/auth/registration/specialist/SpecialistRegistrationLayout';
import { SpecialistStepPersonalDetailsNew } from '@features/auth/registration/specialist/SpecialistStepPersonalDetails';
import { SpecialistResumeStep } from '@features/auth/registration/specialist/SpecialistStepResume';
import { ChatPageWrapper } from '@features/chat';
import {
	DescribeFarmProblems,
	FindSpecialist,
	PackagesStep,
	PaymentStatus,
	PaymentStep,
	SearchStep,
	TermsStep,
} from '@features/FindSpecialist';
import { MonitoringRequestLayout } from '@features/FindSpecialist/MonitoringRequestLayout';
import { MonitoringV2RequestLayout } from '@features/FindSpecialist/MonitoringV2RequestLayout';
import { SpecialistRequestLayout } from '@features/FindSpecialist/SpecialistRequestLayout';
import { ReviewAndSendStep } from '@features/FindSpecialist/steps/ReviewAndSendStep';
import {
	EmailAndPassword,
	FarmerPreferencesNew,
	RegisterEmailConfirmation,
	RoleSelection,
} from '@features/index';
import { DashboardFarmerPage, DashboardSpecialistPage } from '@features/new-dashboard';
import { IncomingRequests } from '@features/requests/incomingRequests/incomingRequests';
import CookiesPolicy from '@pages/Cookies/Cookies';

import { AccountSettings } from '@pages/AccountSettings';
import { AdminPage } from '@pages/AdminPage';
import { Chat } from '@pages/Chat';
import { Farmer } from '@pages/FarmerPage/FarmerPage';
import { FindSpecialistPage } from '@pages/FindSpecialist/FindSpecialistPage';
import { Login } from '@pages/Login';
import {
	MonitoringOngoingProjects,
	MonitoringProtocol,
	MonitoringSpecialists,
	MonitoringTopic,
	MonitoringTopics,
} from '@pages/MonitoringOffer';
import { MonitoringOffer } from '@pages/MonitoringOffer/MonitoringOffer';
import { MonitoringV2Packages, MonitoringV2Payment, MonitoringV2Terms } from '@pages/MonitoringV2';
import { CropAdditionalInformation } from '@pages/MyFarm/components/CropCreation/CropAdditionalInformation';
import { CropCreationLayout } from '@pages/MyFarm/components/CropCreation/CropCreationLayout';
import { CropSelectionForm } from '@pages/MyFarm/components/CropCreation/CropSelectionForm';
import { CropDetails } from '@pages/MyFarm/components/CropDetails/CropDetails';
import { MyFarmMainPage } from '@pages/MyFarm/components/MyFarmMainPage/MyFarmMainPage';
import { MyFarmPage } from '@pages/MyFarm/MyFarmPage';
import { Registration } from '@pages/Registration';
import { Specialist } from '@pages/SpecialistDashboard';

import { ExpressOffersPage } from '@features/FarmerExpressOffers';
import { SeasonalOneTimeOffersPage } from '@features/FarmerSeasonalOneTimeOffers';
import {
	OfferDetailPage as ExpressOfferDetailPage,
	OffersListPage as ExpressOffersListPage,
} from '@features/SpecialistExpressOffers/pages';
import { OfferDetailPage, OffersListPage } from '@features/SpecialistSeasonalOneTimeOffers/pages';
import { ViewOffers } from '@pages/SpecialistOffer/viewOffer/ViewOffer';
import { Navigate, Route } from 'react-router-dom';
import { AuthenticatedGuard, RoleGuard, UnregisteredGuard } from './guards';

import { SpecialistProfile } from '@pages/FarmerOffer/SpecialistProfile';

import { OfferDetailPage as FarmerOfferDetailPageExpress } from '@features/FarmerExpressOffers/pages';
import { OfferDetailPage as FarmerOfferDetailPageSeasonal } from '@features/FarmerSeasonalOneTimeOffers/pages';

export const PublicRoutes = (
	<>
		<Route path="/" element={<Navigate to="/en/login" />} />
		<Route path="/:language" element={<AuthLayout />}>
			<Route
				path="register"
				element={
					<AuthenticatedGuard>
						<Registration />
					</AuthenticatedGuard>
				}
			>
				<Route index element={<Navigate to="role-selection" />} />
				<Route path="role-selection" element={<RoleSelection />} />
				<Route path="email-and-password" element={<EmailAndPassword />} />
				<Route path="email-confirmation" element={<RegisterEmailConfirmation />} />
			</Route>
			<Route
				path="login"
				element={
					<AuthenticatedGuard>
						<Login />
					</AuthenticatedGuard>
				}
			>
				<Route index element={<LoginForm />} />
				<Route path="reset" element={<ResetForm />} />
				<Route path="email-confirmation" element={<LoginEmailConfirmation />} />
				<Route path="reset-password" element={<ResetPassword />} />
				<Route path="congratulations" element={<CongratulationsForm />} />
			</Route>

			<Route path="payment-status" element={<PaymentStatus />} />

			<Route path="cookies-policy" element={<CookiesPolicy />} />
			
			{/* Dedicated 404 route */}
			<Route path="404" element={<NotFoundPage />} />
			
			{/* Catch-all route for invalid paths within public routes */}
			<Route path="*" element={<Navigate to="404" replace />} />
		</Route>
	</>
);

export const SpecialistRoutes = (
	<Route
		path="specialist"
		element={
			<RoleGuard allowedRole="specialist">
				<DashboardLayout />
			</RoleGuard>
		}
	>
		<Route index element={<Navigate to="dashboard" />} />
		<Route path="dashboard" element={<SpecialistActiveGuard><DashboardSpecialistPage /></SpecialistActiveGuard>} />
		<Route path="profile" element={<SpecialistActiveGuard><Specialist /></SpecialistActiveGuard>} />
		<Route path="settings" element={<SpecialistActiveGuard><AccountSettings /></SpecialistActiveGuard>} />
		{/*<Route path="request" element={<Requests />} />*/}
		{/*<Route path="archives" element={<Archives />} />*/}
		<Route path="details/:id" element={<SpecialistActiveGuard><IncomingRequests /></SpecialistActiveGuard>} />
		<Route path="view-offers" element={<SpecialistActiveGuard><ViewOffers /></SpecialistActiveGuard>} />

		{/* Seasonal and One-Time Offers Routes */}
		<Route path="seasonal-onetime">
			<Route path="offers" element={<SpecialistActiveGuard><OffersListPage /></SpecialistActiveGuard>} />
			<Route path="offers/:offerId" element={<SpecialistActiveGuard><OfferDetailPage /></SpecialistActiveGuard>} />
		</Route>

		<Route path="express">
			<Route path="offers" element={<SpecialistActiveGuard><ExpressOffersListPage /></SpecialistActiveGuard>} />
			<Route path="offers/:offerId" element={<SpecialistActiveGuard><ExpressOfferDetailPage /></SpecialistActiveGuard>} />
		</Route>

		<Route path="monitoring">
			<Route path="offers" element={<SpecialistActiveGuard><MonitoringOffer /></SpecialistActiveGuard>} />
			<Route path="ongoing-projects" element={<SpecialistActiveGuard><MonitoringOngoingProjects /></SpecialistActiveGuard>} />

			<Route path="project/:id">
				<Route index element={<SpecialistActiveGuard><MonitoringProtocol /></SpecialistActiveGuard>} />
				<Route path="topics" element={<SpecialistActiveGuard><MonitoringTopics /></SpecialistActiveGuard>} />
				<Route path="topics/:topicId/guidelines" element={<SpecialistActiveGuard><MonitoringTopic /></SpecialistActiveGuard>} />
			</Route>
		</Route>

		<Route path="chat" element={<SpecialistActiveGuard><Chat /></SpecialistActiveGuard>}></Route>

		<Route path="chats" element={<SpecialistActiveGuard><Chat /></SpecialistActiveGuard>}>
			<Route path="individual-chats" element={<Chat />}>
				<Route path=":chatId" element={<Chat />}>
					<Route path="info" element={<Chat />} />
				</Route>
			</Route>
			<Route path="group-chats" element={<Chat />}>
				<Route path=":chatId" element={<Chat />}>
					<Route path="info" element={<Chat />} />
				</Route>
			</Route>
			<Route path="completed-chats" element={<Chat />}>
				<Route path=":chatId" element={<Chat />}>
					<Route path="info" element={<Chat />} />
				</Route>
			</Route>
			<Route path="calls" element={<Chat />}>
				<Route path=":chatId" element={<Chat />}>
					<Route path="info" element={<Chat />} />
					<Route path="callInfo" element={<Chat />} />
				</Route>
			</Route>
		</Route>
	</Route>
);

export const FarmerRoutes = (
	<>
		<Route
			path="farmer"
			element={
				<RoleGuard allowedRole="farmer">
					<DashboardLayout />
				</RoleGuard>
			}
		>
			<Route index element={<Navigate to="dashboard" />} />
			<Route path="dashboard" element={<DashboardFarmerPage />} />

			<Route path="view-offers" element={<ViewOffers />} />

			<Route path="specialist-profile/:id" element={<SpecialistProfile />} />

			<Route path="monitoring">
				<Route path="offers" element={<MonitoringOffer />} />
				<Route path="ongoing-projects" element={<MonitoringOngoingProjects />} />

				<Route path="offers/:id">
					<Route index element={<MonitoringOffer />} />
					<Route path="specialists" element={<MonitoringSpecialists />} />
				</Route>

				<Route path="project/:id">
					<Route index element={<MonitoringProtocol />} />
					<Route path="topics" element={<MonitoringTopics />} />
					<Route path="topics/:topicId/guidelines" element={<MonitoringTopic />} />
				</Route>
			</Route>

			<Route path="express/offers" element={<ExpressOffersPage />} />
			<Route path="express/offers/:offerId" element={<FarmerOfferDetailPageExpress />} />

			<Route path="seasonal-onetime/offers" element={<SeasonalOneTimeOffersPage />} />
			<Route path="seasonal-onetime/offers/:offerId" element={<FarmerOfferDetailPageSeasonal />} />

			<Route path="services" element={<FindSpecialistPage />}>
				<Route index element={<FindSpecialist />} />

				<Route path="specialist-request" element={<SpecialistRequestLayout />}>
					<Route index element={<Navigate to="search" />} />
					<Route path="search" element={<SearchStep />} />
					<Route path="search/:requestId" element={<SearchStep />} />
					<Route path="description/:requestId" element={<DescribeFarmProblems />} />
					<Route path="review-and-send/:requestId" element={<ReviewAndSendStep />} />
				</Route>

				<Route path="monitoring" element={<MonitoringRequestLayout />}>
					<Route index element={<Navigate to="packages" />} />
					<Route path="packages" element={<PackagesStep />} />
					<Route path="terms" element={<TermsStep />} />
					<Route path="payment" element={<PaymentStep />} />
				</Route>

				<Route path="monitoring-v2" element={<MonitoringV2RequestLayout />}>
					<Route index element={<Navigate to="packages" />} />
					<Route path="packages" element={<MonitoringV2Packages />} />
					<Route path="terms" element={<MonitoringV2Terms />} />
					<Route path="payment" element={<MonitoringV2Payment />} />
				</Route>

				<Route path="express" element={<SpecialistRequestLayout />}>
					<Route index element={<Navigate to="search" />} />
					<Route path="search" element={<SearchStep />} />
					<Route path="search/:requestId" element={<SearchStep />} />
					<Route path="description/:requestId" element={<DescribeFarmProblems />} />
					<Route path="review-and-send/:requestId" element={<ReviewAndSendStep />} />
				</Route>
			</Route>

			<Route path="my-farm" element={<MyFarmPage />}>
				<Route index element={<MyFarmMainPage />} />
				<Route path="create" element={<CropCreationLayout />}>
					<Route index element={<Navigate to="crop-selection" />} />
					<Route path="crop-selection" element={<CropSelectionForm />} />
					<Route path="additional-information" element={<CropAdditionalInformation />} />
				</Route>
				<Route path="edit/:id" element={<CropCreationLayout />}>
					<Route index element={<Navigate to="crop-selection" />} />
					<Route path="crop-selection" element={<CropSelectionForm />} />
					<Route path="additional-information" element={<CropAdditionalInformation />} />
				</Route>
				<Route path="details/:id" element={<CropDetails />} />
			</Route>
			<Route path="chats" element={<Chat />}>
				<Route path="individual-chats" element={<Chat />}>
					<Route path=":chatId" element={<Chat />}>
						<Route path="info" element={<Chat />} />
					</Route>
				</Route>
				<Route path="group-chats" element={<Chat />}>
					<Route path=":chatId" element={<Chat />}>
						<Route path="info" element={<Chat />} />
					</Route>
				</Route>
				<Route path="completed-chats" element={<Chat />}>
					<Route path=":chatId" element={<Chat />}>
						<Route path="info" element={<Chat />} />
					</Route>
				</Route>
				<Route path="calls" element={<Chat />}>
					<Route path=":chatId" element={<Chat />}>
						<Route path="info" element={<Chat />} />
						<Route path="callInfo" element={<Chat />} />
					</Route>
				</Route>
			</Route>
			<Route path="profile" element={<Farmer />} />
			<Route path="settings" element={<AccountSettings />} />
		</Route>
	</>
);

export const RegistrationRoutes = (
	<>
		<Route
			path="farmer"
			element={
				<UnregisteredGuard>
					<FarmerRegistrationLayout />
				</UnregisteredGuard>
			}
		>
			<Route path="personal-details" element={<FarmerPersonalDetailsNew />} />
			<Route path="preferences" element={<FarmerPreferencesNew />} />
		</Route>
		<Route
			path="specialist"
			element={
				<UnregisteredGuard>
					<SpecialistRegistrationLayout />
				</UnregisteredGuard>
			}
		>
			<Route path="personal-details" element={<SpecialistStepPersonalDetailsNew />} />
			<Route path="resume" element={<SpecialistResumeStep />} />
		</Route>
		<Route
			path="company"
			element={
				<UnregisteredGuard>
					<CompanyRegistrationLayout />
				</UnregisteredGuard>
			}
		>
			<Route path="personal-details" element={<CompanyStepPersonalDetails />} />
			<Route path="resume" element={<CompanyRegistrationResumeStep />} />
		</Route>
		<Route path="farmer-profile-update" element={<FarmerPersonalDetailsNew />} />
	</>
);

export const ChatRoutes = (
	<>
		<Route path="chat" element={<ChatPageWrapper />} />
	</>
);

export const AdminRoutes = (
	<Route
		path="admin"
		element={
			<RoleGuard allowedRole="admin">
				<AdminPage />
			</RoleGuard>
		}
	/>
);
